import { Component, Event, EventEmitter, JSX, Prop, h } from "@stencil/core";
import { NjOfferData } from "../../global/JourneyStore";
import { i18n } from "../../utils/I18n";
import { FromToData } from "../nj-from-to/nj-from-to";
import { trackMatomoEvent } from "../../utils/matomo";
import { DATA_STORE_KEY_OUTWARD_VALUE, DATA_STORE_KEY_RETURN_VALUE, dataStore } from "../../global/DataStore";

@Component({
    tag: "nj-selection-summary",
    styleUrl: "nj-selection-summary.scss",
    shadow: true
})

export class NjSelectionSummary {
    @Prop() fromToData: FromToData;
    @Prop() isSelectionCompleted: boolean;
    @Prop() outwardOffer: NjOfferData | null;
    @Prop() returnOffer: NjOfferData | null;

    @Event() scrollToDirection: EventEmitter<string>;

    private handleNoSelectionClick(e: Event, direction: 'outward' | 'return') {
        e.preventDefault();
        this.scrollToDirection.emit(direction)
    }
    
    private renderSelectedConnection(selectedOffer: NjOfferData, direction: 'outward' | 'return'): JSX.Element {
        const label = i18n.getMessage(`home.${direction}-journey`);

        return selectedOffer
            ? this.renderSelected(selectedOffer, label)
            : this.renderNotSelected(direction, label);
    }

    private renderSelected(selectedOffer: NjOfferData, label: string): JSX.Element {
        return <p class="summary__item">
            <h4 class="summary__term">
                {label}{' '}
            </h4>
            <div class="summary__connection">
                <check-icon circled width={14} height={14} />
                <nj-connection-overview
                    showCalendar={false}
                    departureDate={selectedOffer.connection.departure}
                    arrivalDate={selectedOffer.connection.arrival}
                    departureStation={selectedOffer.connection.from.name}
                    arrivalStation={selectedOffer.connection.to.name}
                    trains={[selectedOffer.connection.train]}
                >
				</nj-connection-overview>
            </div>
        </p>;
    }

    private renderNotSelected(direction: 'outward' | 'return', label: string): JSX.Element {
        return <p class="summary__item summary__item--not-selected">
            <h4 class="summary__term">
                {label}{' '}</h4>
            <span class="summary__definition">
                <edit-icon width={15} aria-hidden="true" />
                {i18n.getMessage('home.not-selected')}
            </span> &nbsp; - &nbsp;
            <a class="summary__backlink" href={`#${direction}-journey`} onClick={(e) => this.handleNoSelectionClick(e, direction)}>
                {i18n.getMessage(`home.select.${direction}`)}
            </a>
        </p>
    }

    private renderValidationError(): JSX.Element {
        if (this.isSelectionCompleted !== false) {
            return null;
        }
        const validationMessages = [
            {
                condition: !this.outwardOffer && !this.returnOffer && this.fromToData.dateReturn,
                message: i18n.getMessage('home.validation-message'),
            },
            {
                condition: !this.returnOffer && this.fromToData.dateReturn,
                message: i18n.getMessage('home.validation-message.return-journey'),
            },
            {
                condition: !this.outwardOffer,
                message: i18n.getMessage('home.validation-message.outward-journey'),
            },
        ];

        for (const { condition, message } of validationMessages) {
            if (condition) {
                return (
                    <nj-validation type="ERROR" alert={true}>
                        {message}
                    </nj-validation>
                );
            }
        }

        return null;
    }

    submitConnections(e: Event, pathAfterSubmit: string) {
        e.preventDefault();
        trackMatomoEvent({ "event": "nj.home.categorySelected" });
        if (this.fromToData.dateOutward && this.fromToData.dateReturn) {
            if (!this.outwardOffer || !this.returnOffer) {
                this.isSelectionCompleted = false;
            } else {
                this.isSelectionCompleted = true;
            }
        } else {
            if (!this.outwardOffer) {
                this.isSelectionCompleted = false;
            } else {
                this.isSelectionCompleted = true;
            }
        }
        if (this.isSelectionCompleted) {
            dataStore().removeSessionData(DATA_STORE_KEY_OUTWARD_VALUE);
            dataStore().removeSessionData(DATA_STORE_KEY_RETURN_VALUE);
            location.href = pathAfterSubmit ? pathAfterSubmit : '#/reservierung';
        }
    }

    render() {
        return (
            <div class="js-summary">
                <h3>{i18n.getMessage("home.header-selection")}</h3>
                <div class="summary">
                    <div>
                        {this.renderValidationError()}
                        {this.renderSelectedConnection(this.outwardOffer, 'outward')}
                        {this.fromToData.dateReturn ? this.renderSelectedConnection(this.returnOffer, 'return') : null}
                    </div>
                    <button onClick={(e: Event) => this.submitConnections(e, "#/reservierung")}>
                        {i18n.getMessage("home.next-step")}&nbsp;
                        <arrow-right-icon />
                    </button>
                </div>
            </div>
        );
    }
}
